/**
 * Custom Fields API service - handles custom field API calls
 */

import { apiClient } from './client';

// Types
export type FieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'datetime'
  | 'select'
  | 'multiselect'
  | 'checkbox'
  | 'url'
  | 'email'
  | 'user';

export interface FieldOption {
  value: string;
  label: string;
  color?: string;
}

export interface FieldValidation {
  min?: number;
  max?: number;
  pattern?: string;
  message?: string;
}

export interface CustomField {
  id: string;
  projectId: string;
  name: string;
  key: string;
  fieldType: FieldType;
  description?: string;
  isRequired: boolean;
  position: number;
  defaultValue?: any;
  options?: FieldOption[];
  validation?: FieldValidation;
  showOnCard: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomFieldValue {
  id: string;
  taskId: string;
  customFieldId: string;
  value: any;
  createdAt: string;
  updatedAt: string;
}

export interface CustomFieldWithValue extends CustomField {
  currentValue?: any;
}

export interface CreateCustomFieldRequest {
  name: string;
  key: string;
  fieldType: FieldType;
  description?: string;
  isRequired?: boolean;
  position?: number;
  defaultValue?: any;
  options?: FieldOption[];
  validation?: FieldValidation;
  showOnCard?: boolean;
}

export interface UpdateCustomFieldRequest {
  name?: string;
  description?: string;
  isRequired?: boolean;
  position?: number;
  defaultValue?: any;
  options?: FieldOption[];
  validation?: FieldValidation;
  showOnCard?: boolean;
}

/**
 * Custom Fields API methods
 */
export const customFieldsApi = {
  // =====================
  // Custom Field CRUD
  // =====================

  /**
   * List custom fields for a project
   */
  async list(projectId: string): Promise<CustomField[]> {
    const response = await apiClient.get<any[]>(
      `/projects/${projectId}/custom-fields`
    );
    return response.data.map(transformCustomField);
  },

  /**
   * Get a custom field by ID
   */
  async get(projectId: string, fieldId: string): Promise<CustomField> {
    const response = await apiClient.get<any>(
      `/projects/${projectId}/custom-fields/${fieldId}`
    );
    return transformCustomField(response.data);
  },

  /**
   * Create a new custom field
   */
  async create(
    projectId: string,
    data: CreateCustomFieldRequest
  ): Promise<CustomField> {
    const response = await apiClient.post<any>(
      `/projects/${projectId}/custom-fields`,
      {
        name: data.name,
        key: data.key,
        field_type: data.fieldType,
        description: data.description,
        is_required: data.isRequired,
        position: data.position,
        default_value: data.defaultValue,
        options: data.options,
        validation: data.validation,
        show_on_card: data.showOnCard,
      }
    );
    return transformCustomField(response.data);
  },

  /**
   * Update a custom field
   */
  async update(
    projectId: string,
    fieldId: string,
    data: UpdateCustomFieldRequest
  ): Promise<CustomField> {
    const payload: any = {};
    if (data.name !== undefined) payload.name = data.name;
    if (data.description !== undefined) payload.description = data.description;
    if (data.isRequired !== undefined) payload.is_required = data.isRequired;
    if (data.position !== undefined) payload.position = data.position;
    if (data.defaultValue !== undefined) payload.default_value = data.defaultValue;
    if (data.options !== undefined) payload.options = data.options;
    if (data.validation !== undefined) payload.validation = data.validation;
    if (data.showOnCard !== undefined) payload.show_on_card = data.showOnCard;

    const response = await apiClient.patch<any>(
      `/projects/${projectId}/custom-fields/${fieldId}`,
      payload
    );
    return transformCustomField(response.data);
  },

  /**
   * Delete a custom field
   */
  async delete(projectId: string, fieldId: string): Promise<void> {
    await apiClient.delete(`/projects/${projectId}/custom-fields/${fieldId}`);
  },

  /**
   * Reorder custom fields
   */
  async reorder(projectId: string, fieldIds: string[]): Promise<CustomField[]> {
    const response = await apiClient.post<any[]>(
      `/projects/${projectId}/custom-fields/reorder`,
      fieldIds
    );
    return response.data.map(transformCustomField);
  },

  // =====================
  // Custom Field Values
  // =====================

  /**
   * Get all custom fields with values for a task
   */
  async getTaskFields(taskId: string): Promise<CustomFieldWithValue[]> {
    const response = await apiClient.get<any[]>(`/tasks/${taskId}/custom-fields`);
    return response.data.map(transformCustomFieldWithValue);
  },

  /**
   * Set a custom field value for a task
   */
  async setValue(
    taskId: string,
    fieldId: string,
    value: any
  ): Promise<CustomFieldValue> {
    const response = await apiClient.put<any>(
      `/tasks/${taskId}/custom-fields/${fieldId}`,
      value
    );
    return transformCustomFieldValue(response.data);
  },

  /**
   * Delete a custom field value from a task
   */
  async deleteValue(taskId: string, fieldId: string): Promise<void> {
    await apiClient.delete(`/tasks/${taskId}/custom-fields/${fieldId}`);
  },

  /**
   * Bulk update custom field values for a task
   */
  async bulkSetValues(
    taskId: string,
    values: Record<string, any>
  ): Promise<CustomFieldValue[]> {
    const response = await apiClient.patch<any[]>(
      `/tasks/${taskId}/custom-fields`,
      values
    );
    return response.data.map(transformCustomFieldValue);
  },
};

// Transform functions
// Note: Backend uses alias_generator=to_camel, so API responses are camelCase
function transformCustomField(data: any): CustomField {
  return {
    id: data.id,
    projectId: data.projectId ?? data.project_id,
    name: data.name,
    key: data.key,
    fieldType: data.fieldType ?? data.field_type,
    description: data.description,
    isRequired: data.isRequired ?? data.is_required,
    position: data.position,
    defaultValue: data.defaultValue ?? data.default_value,
    options: data.options,
    validation: data.validation,
    showOnCard: data.showOnCard ?? data.show_on_card,
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
}

function transformCustomFieldValue(data: any): CustomFieldValue {
  return {
    id: data.id,
    taskId: data.taskId ?? data.task_id,
    customFieldId: data.customFieldId ?? data.custom_field_id,
    value: data.value,
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
}

function transformCustomFieldWithValue(data: any): CustomFieldWithValue {
  return {
    ...transformCustomField(data),
    currentValue: data.currentValue ?? data.current_value,
  };
}

export default customFieldsApi;
